import { useState, useEffect, useCallback, useMemo } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import universityIcon from '../images/icono.png';
import styles from './DirectorioUniversidades.module.css';
import FiltroTipos from './FiltroTipos';

const cleanPhoneNumber = (phone) => {
    if (!phone || typeof phone !== 'string') return phone;
    
    let cleaned = phone.replace(/[\s\-\(\)]/g, '');
    
    if (cleaned.startsWith('+52')) {
        cleaned = cleaned.substring(3);
    }
    else if (cleaned.startsWith('52') && cleaned.length === 12) {
        cleaned = cleaned.substring(2);
    }
    
    return cleaned;
};

const cleanPhoneArray = (phoneArray) => {
    if (!Array.isArray(phoneArray)) return phoneArray;
    return phoneArray.map(phone => cleanPhoneNumber(phone));
};

const MapasUniversidades = () => {
    const [universidadesOriginales, setUniversidadesOriginales] = useState([]); 
    const [tiposUniversidades, setTiposUniversidades] = useState([]);
    const [tiposSeleccionados, setTiposSeleccionados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Estados para controlar los menús desplegables (abiertos en PC, cerrados en móvil)
    const [mostrarFiltros, setMostrarFiltros] = useState(window.innerWidth > 768);
    const [mostrarLeyenda, setMostrarLeyenda] = useState(window.innerWidth > 768);
    
    // Constantes memoizadas
    const CENTER_POSITION = useMemo(() => [20.6067691, -100.4515037], []);

    const LEYENDA_TIPOS = [
    { color: 'red', nombre: 'Autónoma' },
    { color: 'green', nombre: 'Tecnológico' },
    { color: 'gold', nombre: 'Tecnológica / Politécnica' },
    { color: 'violet', nombre: 'Privada' },
    { color: 'blue', nombre: 'Normal / Aeronáutica / Pedagógica' }
];
    
    // Función para asignar un pin de color según el tipo de universidad
    // Función optimizada usando los IDs de la base de datos
    const getCustomIcon = useCallback((tipoId) => {
        let iconUrl = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png'; // Azul por defecto

        switch (tipoId) {
            case 1: // Autónoma (ej. UAQ)
                iconUrl = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png'; // Rojo
                break;
                
            case 7: // Tecnológico (Sección TecNM)
                iconUrl = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png'; // Verde 
                break;
                
            case 3: // Tecnológica
            case 6: // Politécnica (¡Como la UPSRJ!)
                iconUrl = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-gold.png'; // Dorado/Amarillo 
                break;
                
            case 8: // Privada
                iconUrl = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-violet.png'; // Violeta 
                break;

            default:
                // IDs 2 (Normal), 4 (Aeronáutica), 5 (Pedagógica) se quedan con el azul estándar
                iconUrl = 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png'; // Azul 
                break;
        }

        return L.icon({
            iconUrl: iconUrl,
            shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            shadowSize: [41, 41]
        });
    }, []);
    // Memoizar universidades filtradas
    const universidadesFiltradas = useMemo(() => {
        if (tiposSeleccionados.length === 0) {
            return [];
        }
        return universidadesOriginales.filter(universidad => 
            tiposSeleccionados.includes(universidad.tipo?.id)
        );
    }, [universidadesOriginales, tiposSeleccionados]);

    // Validadores optimizados
    const isValidAddress = useCallback((address) => {
        if (!address || typeof address !== 'string' || address.length <= 5) return false;
        
        // Usar una sola regex más eficiente
        const invalidPatterns = /^(https?:\/\/|[^\s@]+@[^\s@]+\.[^\s@]+|[\d\s\-\+\(\)]+)$/i;
        return !invalidPatterns.test(address);
    }, []);

    // Extractor de coordenadas mejorado y optimizado
    const extractCoords = useCallback((embedUrl) => {
        if (!embedUrl) return null;

        const decodedUrl = decodeURIComponent(embedUrl);

        // MÉTODO 1 (PRIORITARIO): Patrón !2d[lng]!3d[lat] - el más común en embeds
        const embedPattern = /!2d(-?\d+\.?\d*)!3d(-?\d+\.?\d*)/;
        const embedMatch = embedPattern.exec(decodedUrl);
        
        if (embedMatch) {
            return [parseFloat(embedMatch[2]), parseFloat(embedMatch[1])]; // [lat, lng]
        }

        // MÉTODO 2: Coordenadas específicas !3d[lat]!4d[lng]
        const specificPattern = /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/;
        const specificMatch = specificPattern.exec(decodedUrl);
        
        if (specificMatch) {
            return [parseFloat(specificMatch[1]), parseFloat(specificMatch[2])];
        }

        // MÉTODO 3: Patrón @lat,lng,zoom
        const atPattern = /@(-?\d+\.?\d*),(-?\d+\.?\d*),(\d+\.?\d*)/;
        const atMatch = atPattern.exec(decodedUrl);
        
        if (atMatch) {
            return [parseFloat(atMatch[1]), parseFloat(atMatch[2])];
        }

        // MÉTODO 4: Place ID con coordenadas siguientes
        const placeIdPattern = /!1s0x[a-f0-9]+:0x[a-f0-9]+.*?!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/;
        const placeIdMatch = placeIdPattern.exec(decodedUrl);
        
        if (placeIdMatch) {
            return [parseFloat(placeIdMatch[1]), parseFloat(placeIdMatch[2])];
        }

        return null;
    }, []);

    // Geocoding optimizado con retry y cache
    const geocodeCache = useMemo(() => new Map(), []);
    
    const geocodeWithContext = useCallback(async (universityName, address) => {
        if (!isValidAddress(address)) return null;

        const cacheKey = `${universityName}_${address}`;
        if (geocodeCache.has(cacheKey)) {
            return geocodeCache.get(cacheKey);
        }

        try {
            const query = `${universityName} ${address} Querétaro México`;
            
            // Rate limiting más agresivo
            await new Promise(resolve => setTimeout(resolve, 500));
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1&countrycodes=mx`,
                {
                    headers: { 'User-Agent': 'UniversitiesMap/1.0' },
                    signal: controller.signal
                }
            );
            
            clearTimeout(timeoutId);
            
            if (!res.ok) return null;
            
            const [first] = await res.json();
            const coords = first ? [parseFloat(first.lat), parseFloat(first.lon)] : null;
            
            geocodeCache.set(cacheKey, coords);
            
            return coords;
            
        } catch (err) {
            geocodeCache.set(cacheKey, null);
            return null;
        }
    }, [isValidAddress, geocodeCache]);

    // Fetch optimizado con Promise.allSettled para manejar errores individuales
    const fetchTiposUniversidades = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/universidades/obtener-tipos-universidades`);
            if (!res.ok) throw new Error(`Error HTTP: ${res.status}`);
            return await res.json();
        } catch (err) {
            throw new Error('Error al cargar tipos de universidades');
        }
    }, []);

    const fetchUniversidades = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/universidades/obtener-universidades`);
            if (!res.ok) throw new Error(`Error HTTP: ${res.status}`);
            const data = await res.json();

            // Procesar coordenadas en paralelo pero con límite de concurrencia
            const processInBatches = async (items, batchSize = 3) => {
                const results = [];
                for (let i = 0; i < items.length; i += batchSize) {
                    const batch = items.slice(i, i + batchSize);
                    const batchResults = await Promise.allSettled(
                        batch.map(async (universidad) => {
                            const plantelesConCoords = await Promise.allSettled(
                                universidad.planteles.map(async (plantel) => {
                                    try {
                                        let coords = await extractCoords(plantel.mapEmbed);
                                        
                                        if (!coords && plantel.address && isValidAddress(plantel.address)) {
                                            coords = await geocodeWithContext(universidad.name, plantel.address);
                                        }
                                        
                                        return { ...plantel, coordinates: coords };
                                    } catch (error) {
                                        return { ...plantel, coordinates: null };
                                    }
                                })
                            );

                            return {
                                ...universidad,
                                planteles: plantelesConCoords
                                    .filter(result => result.status === 'fulfilled')
                                    .map(result => result.value)
                            };
                        })
                    );
                    
                    results.push(...batchResults
                        .filter(result => result.status === 'fulfilled')
                        .map(result => result.value)
                    );
                }
                return results;
            };

            return await processInBatches(data);

        } catch (err) {
            throw new Error('Error al cargar universidades');
        }
    }, [extractCoords, geocodeWithContext, isValidAddress]);

    // Handlers optimizados
    const handleTipoChange = useCallback((tipoId) => {
        setTiposSeleccionados(prev => 
            prev.includes(tipoId)
                ? prev.filter(id => id !== tipoId)
                : [...prev, tipoId]
        );
    }, []);

    const handleToggleAll = useCallback(() => {
        setTiposSeleccionados(prev => 
            prev.length === tiposUniversidades.length 
                ? [] 
                : tiposUniversidades.map(tipo => tipo.id)
        );
    }, [tiposUniversidades]);

    // Effect principal optimizado
    useEffect(() => {
        const initializeData = async () => {
            setLoading(true);
            setError(null);
            
            try {
                // Cargar en paralelo pero manejar errores independientemente
                const [tiposResult, universidadesResult] = await Promise.allSettled([
                    fetchTiposUniversidades(),
                    fetchUniversidades()
                ]);

                if (tiposResult.status === 'fulfilled') {
                    const tipos = tiposResult.value;
                    setTiposUniversidades(tipos);
                    setTiposSeleccionados(tipos.map(tipo => tipo.id));
                } else {
                    throw tiposResult.reason;
                }

                if (universidadesResult.status === 'fulfilled') {
                    setUniversidadesOriginales(universidadesResult.value);
                } else {
                    throw universidadesResult.reason;
                }

            } catch (error) {
                setError(error.message || 'Error al cargar datos');
            } finally {
                setLoading(false);
            }
        };
        
        initializeData();
    }, [fetchUniversidades, fetchTiposUniversidades]);

    // Componentes de estado memoizados
    const LoadingComponent = useMemo(() => (
        <div className={styles.loadingContainer}>
            <div className={styles.loadingContent}>
                <div className={styles.loadingSpinner}></div>
                <p className={styles.loadingText}>Cargando universidades...</p>
            </div>
        </div>
    ), []);

    const ErrorComponent = useMemo(() => (
        <div className={styles.loadingContainer}>
            <div className={styles.loadingContent}> 
                <p className={styles.loadingText}>Error al cargar mapa :(</p>
                <button 
                    onClick={() => window.location.reload()} 
                    className={styles.retryButton}
                >
                    Reintentar
                </button>
            </div>
        </div>
    ), []);

    if (loading) return LoadingComponent;
    if (error) return ErrorComponent;

    return (
        <div style={{ position: 'relative', height: '100vh', width: '100%' }}>
            <MapContainer
                center={CENTER_POSITION}
                zoom={12}
                style={{ height: '100vh', width: '100%' }}
                scrollWheelZoom={true}
            >
                <TileLayer
                    attribution="© OpenStreetMap contributors"
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {/* Renderizado de los marcadores en el mapa */}
                {universidadesFiltradas.map(uni => (
                    uni.planteles
                        .filter(p => p.coordinates)
                        .map(p => (
                            <Marker
                                key={`${uni.id}-${p.id}`}
                                position={p.coordinates}
                                icon={getCustomIcon(uni.tipo?.id)}
                            >
                                <Popup>
                                    <div>
                                        <strong>{uni.name}</strong><br />
                                        <em>{uni.tipo?.nombre}</em><br />
                                        {p.address && (
                                            <>
                                                <strong>Dirección:</strong> {p.address}<br />
                                            </>
                                        )}
                                        {p.phone?.length > 0 && (
                                            <>
                                                <strong>Teléfono:</strong> {cleanPhoneArray(p.phone).join(', ')}<br />
                                            </>
                                        )}
                                        {p.email?.length > 0 && (
                                            <>
                                                <strong>Email:</strong> {p.email.join(', ')}<br />
                                            </>
                                        )}
                                        {uni.contact?.website && (
                                            <>
                                                <strong>Sitio web:</strong> 
                                                <a 
                                                    href={uni.contact.website} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                >
                                                    {uni.contact.website}
                                                </a>
                                            </>
                                        )}
                                    </div>
                                </Popup>
                            </Marker>
                        ))
                ))}
            </MapContainer>

            {/* ========================================================================= */}
            {/* CAPAS DE INTERFAZ ABSOLUTAS (Responsivas y Desplegables)                  */}
            {/* ========================================================================= */}
            <div style={{
                position: 'absolute',
                top: '90px',
                left: '10px',
                zIndex: 1100,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                width: '90%',
                maxWidth: '320px' // En PC no crecerá más de esto, en móvil ocupará el 90%
            }}>
                {/* PANEL DE FILTROS DESPLEGABLE */}
                <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                    <button
                        onClick={() => setMostrarFiltros(!mostrarFiltros)}
                        style={{ width: '100%', padding: '12px 15px', background: '#242b57', color: 'white', border: 'none', textAlign: 'left', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <span>Filtros de Universidad</span>
                        <span>{mostrarFiltros ? '▼' : '▶'}</span>
                    </button>
                    
                    {mostrarFiltros && (
                        <div style={{ padding: '10px', maxHeight: '40vh', overflowY: 'auto' }}>
                            <FiltroTipos 
                                tipos={tiposUniversidades}
                                tiposSeleccionados={tiposSeleccionados}
                                onTipoChange={handleTipoChange}
                                onToggleAll={handleToggleAll}
                            />
                        </div>
                    )}
                </div>

                {/* PANEL DE LEYENDA DESPLEGABLE */}
                <div style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
                    <button
                        onClick={() => setMostrarLeyenda(!mostrarLeyenda)}
                        style={{ width: '100%', padding: '12px 15px', background: '#242b57', color: 'white', border: 'none', textAlign: 'left', fontWeight: 'bold', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <span>Tipos de Universidad</span>
                        <span>{mostrarLeyenda ? '▼' : '▶'}</span>
                    </button>
                    
                    {mostrarLeyenda && (
                        <div style={{ padding: '15px', maxHeight: '30vh', overflowY: 'auto' }}>
                            {LEYENDA_TIPOS.map(item => (
                                <div
                                    key={item.nombre}
                                    style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}
                                >
                                    <img
                                        src={`https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${item.color}.png`}
                                        alt={item.nombre}
                                        width="18"
                                    />
                                    <span style={{ fontSize: '13px', color: '#444' }}>{item.nombre}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MapasUniversidades;